// Mixpanel Configuration
// Replace YOUR_MIXPANEL_PROJECT_TOKEN with your actual Mixpanel project token
import { createFileLogger } from '../utils/logger';

export const MIXPANEL_CONFIG = {
  // Mixpanel token will be loaded dynamically via configService
  // No hardcoded tokens to avoid confusion
  projectToken: '', // Loaded dynamically via configService
  
  // Environment detection
  environment: (process.env.NODE_ENV as 'development' | 'production') || 'development',
  
  // Session recording settings - Enable in both development and production
  sessionRecording: {
    enabled: true, // Enable by default for all environments
    debugMode: process.env.NODE_ENV === 'development',
  },
  
  // Debug mode - shows console logs
  debugMode: process.env.NODE_ENV === 'development',
  
  // Privacy settings - Show all text in session replays
  privacy: {
    // Don't mask any text - show everything in session replays
    maskAllText: false,
    
    // CSS class to mark elements as sensitive (will be blocked from recording) - keep minimal
    sensitiveClass: 'mp-sensitive',
    
    // Sensitive elements that should be completely blocked from recording
    sensitiveSelector: '[data-sensitive], .password, .credit-card, .ssn, .auth-form, .payment-form',
    
    // Don't collect IP addresses
    collectIP: false,
    
    // Selector for elements that SHOULD be masked (passwords, tokens only)
    // By default Mixpanel masks EVERYTHING, so we specify what to mask instead of what to unmask
    maskPasswordsAndTokensSelector: 'input[type="password"], input[name*="password"], input[name*="token"], input[name*="secret"], .password, .auth-token, .api-key, [data-mask], [data-sensitive-password]',
  },
  
  // Performance settings
  performance: {
    // Stop recording after 30 MINUTES of inactivity (allows users to take breaks)
    idleTimeoutMs: 1800000,
    
    // Maximum 24 HOURS of recording per session (capture full workday sessions)
    maxRecordingMs: 86400000,
    
    // Minimum 2 minutes to save replay (automatically discard short bounces)
    minRecordingMs: 120000,
    
    // Don't collect fonts to reduce payload size
    collectFonts: false,
  }
} as const;

// Helper function to detect if running on localhost
export const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  
  const hostname = window.location.hostname.toLowerCase();
  
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.local') ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    (hostname.startsWith('172.') && 
     parseInt(hostname.split('.')[1]) >= 16 && 
     parseInt(hostname.split('.')[1]) <= 31)
  );
};


// Helper to detect production rivereditor.com hosts
export const isRiverEditorProdHost = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'rivereditor.com' || hostname.endsWith('.rivereditor.com');
};

// Helper to get environment-specific settings with dynamic token loading
export const getMixpanelConfig = async () => {
  const log = createFileLogger('MIXPANEL_CONFIG');
  
  // For localhost - disable Mixpanel (no tracking on localhost)
  if (isLocalhost()) {
    log.info(`🚫 Mixpanel DISABLED on localhost: ${typeof window !== 'undefined' ? window.location.hostname : 'unknown'}`);
    return null;
  }
  
  try {
    // Load Mixpanel token dynamically from configService
    const { configService } = await import('../services/configService');
    const projectToken = await configService.getMixpanelToken();
    
    if (!projectToken) {
      log.warn('No Mixpanel token available from backend, tracking disabled');
      return null;
    }
    
    // We're on production - enable session replay
    log.debug(`Mixpanel enabled for production host: ${typeof window !== 'undefined' ? window.location.hostname : 'unknown'}`);

    return {
      projectToken,
      environment: MIXPANEL_CONFIG.environment as 'development' | 'production',
      sessionRecording: {
        ...MIXPANEL_CONFIG.sessionRecording,
        enabled: true // Always enabled for production
      },
      debugMode: MIXPANEL_CONFIG.debugMode,
    };
    
  } catch (error) {
    log.error('Failed to load Mixpanel configuration:', error);
    return null;
  }
}; 